import { useLanguage, useT } from '../i18n/i18n';

export const PRIVACY_UPDATED = '21 October 2026';

export default function Privacy() {
  const t = useT();
  const { lang } = useLanguage();
  return (
    <div className="prose-terms">
      {lang !== 'en' && (
        <p className="field-hint">{t('This is a translation for your convenience. The English version is the one that applies.')}</p>
      )}
      <p className="field-hint">{t('Last updated {date}', { date: PRIVACY_UPDATED })}</p>

      <h3>{t('Who we are')}</h3>
      <p>
        {t('EasyLearning is run by Fractalon Limited, company number 15785709, registered office 20 Braddon Road, Woodley, Stockport, SK6 1RE, United Kingdom. We decide how your information is used, so we are its controller.')}
      </p>
      <p>
        {t('If you have a question about your information, email')}{' '}
        <a href="mailto:support@fractalon.net">support@fractalon.net</a>.
      </p>
      <p>
        {t("If you use EasyLearning through a school, nursery, therapy practice or council, that organisation decides how its learners' information is used. Ask them first. We follow their instructions.")}
      </p>

      <h3>{t('What we keep about you, the adult')}</h3>
      <ul>
        <li>{t('Your email address and name, so you can sign in and so we can email you about your account.')}</li>
        <li>{t('Your password, kept only in a scrambled form that cannot be turned back into your password.')}</li>
        <li>{t('Your settings, such as text size, colours, voice and your supervisor PIN (also scrambled).')}</li>
        <li>{t('Who you share your account with, and the email addresses you invite.')}</li>
        <li>{t("What you pay for. Card details are entered on Stripe's page. We never see or keep them.")}</li>
      </ul>

      <h3>{t('What we keep about the learners you add')}</h3>
      <ul>
        <li>{t('The name you give each learner. You can use initials or a first name only.')}</li>
        <li>{t('The programs you set up for them, and the words, pictures and prompts in them.')}</li>
        <li>{t('How each practice went: what was shown, the answer, whether it was right first time, how long it took and how much help was given.')}</li>
        <li>{t('Rewards, weekly plans and notes, Talk Board words and preference results.')}</li>
        <li>{t("Photos and voice recordings you choose to add. Please do not photograph learners' faces.")}</li>
      </ul>
      <p>
        {t("We do not ask for anyone's age, date of birth, address or diagnosis. Learners never sign in. We use this information only to run EasyLearning for you: to show the games, record progress, and show you reports and plans.")}
      </p>

      <h3>{t('Our legal reasons')}</h3>
      <ul>
        <li>{t('To provide the service you signed up for (a contract with you): your account, your learners, the games, progress and payments.')}</li>
        <li>{t('Your consent for anything you choose to send to EasyLearning AI. You choose each time, and you can stop at any time.')}</li>
        <li>{t('Your explicit consent, given for each learner, because how a learner practises can show that they have special educational needs, which the law treats as health information. You can withdraw it at any time by removing the learner in Settings.')}</li>
        <li>{t('Our legitimate interest in keeping EasyLearning secure and working, and in answering requests you send us.')}</li>
        <li>{t('The law, which requires us to keep records of payments for 6 years.')}</li>
      </ul>

      <h3>{t('Who else receives information')}</h3>
      <p>
        {t('We never sell information and never use it for advertising. EasyLearning loads nothing from advertising or tracking companies. These companies help us run EasyLearning. Each one may use your information only to do that job for us, under a written agreement:')}
      </p>
      <ul>
        <li><strong>Hostinger</strong> {t('runs the server where EasyLearning and its information are stored, in Manchester, United Kingdom.')}</li>
        <li><strong>Brevo</strong> {t('sends our emails. It receives your email address and name, and what you write in a game request. It is in France.')}</li>
        <li><strong>Stripe</strong> {t('takes payments. It receives your email address, what you buy and your card details. It works in the United Kingdom, the European Union and the United States.')}</li>
        <li><strong>Google Cloud</strong> {t('turns the words and phrases in your programs and Talk Board into speech. It receives only that text and sends back the sound, which we keep on our own server. If you type a name into a program, that name is sent too. It works in the European Union.')}</li>
        <li><strong>Amazon Web Services</strong> {t('keeps a copy of our backups in London, United Kingdom. The copy is locked with a key only we hold, so they cannot read it.')}</li>
      </ul>
      <p>
        {t('Sign-in and speech-to-text run on our own server, so no other company is involved.')}
      </p>

      <h3>{t('EasyLearning AI (only if you choose to use it)')}</h3>
      <ul>
        <li>{t('EasyLearning AI is run for us by Anthropic, in the United States. You agree to its terms before you first use it.')}</li>
        <li>{t('We send only what you choose. Before anything leaves our server, we remove names, email addresses, phone numbers, postcodes and dates from what you type.')}</li>
        <li>{t("We never send a learner's name or age. Scores and other learner information are sent only if you tick them.")}</li>
        <li>{t('If you use a photo in Studio, your browser checks it for faces first and removes its location, then it goes to Anthropic. We delete it as soon as your game is made.')}</li>
        <li>{t("For researched advice, Anthropic's search provider receives the search terms only.")}</li>
        <li>{t('Voice commands are turned into text on our own server in the UK. The recording is not kept and does not go to Anthropic.')}</li>
        <li>{t('Anthropic does not use this information to train its models.')}</li>
        <li>{t('After every request you can open "Where did my data go?" to see exactly what was sent, where, and what came back. We keep that record until you delete your account.')}</li>
      </ul>

      <h3>{t('Sending information outside the UK')}</h3>
      <p>
        {t("Stripe may handle information in the United States. When it does, the information is protected by safeguards approved by the UK in Stripe's agreement with us. Brevo is in the European Union, which the UK recognises as protecting information to the same standard.")}
        {' '}{t('Google Cloud makes our spoken words in the European Union too.')}
        {' '}{t("If you use EasyLearning AI, what you send goes to Anthropic in the United States, protected by the UK's approved safeguards in Anthropic's data processing agreement.")}
      </p>

      <h3>{t('How long we keep things')}</h3>
      <ul>
        <li>{t("Your account and your learners' information: until you delete them, or until nobody has signed in for 24 months. We email you 30 days before deleting an unused account.")}</li>
        <li>{t('Photos and recordings you add: until you delete them, or 30 days after your plan ends.')}</li>
        <li>{t('Payment records, including the customer record Stripe keeps for us: 6 years, because the law requires it. This continues after you delete your account.')}</li>
        <li>{t('Backups: copies made to protect against loss are deleted after 30 days.')}</li>
        <li>{t('Spoken versions of the words in your programs and Talk Board, made by Google Cloud (or on our own server for the earlier voices) and kept on our own server: kept until you delete your account. A common word that other families also use stays for them.')}</li>
        <li>{t('Records of visits to our server, which include internet addresses: deleted after 14 days.')}</li>
      </ul>
      <p>
        {t('Deleting your account (Settings, then Account) removes your sign-in, your learners and everything recorded about them straight away, apart from payment records, which we must keep.')}
      </p>

      <h3>{t('Your rights')}</h3>
      <p>{t('You can ask us to:')}</p>
      <ul>
        <li>{t('give you a copy of the information we hold about you (you can also download it yourself from Settings, then Account);')}</li>
        <li>{t('correct anything that is wrong (you can change most things yourself);')}</li>
        <li>{t('delete your information (you can delete a learner, or your whole account, yourself);')}</li>
        <li>{t('stop or limit how we use it, or object to how we use it;')}</li>
        <li>{t('give you your information in a file you can take to another service.')}</li>
      </ul>
      <p>
        {t('Email')} <a href="mailto:support@fractalon.net">support@fractalon.net</a>. {t('We will reply within one month. It is free.')}
      </p>
      <p>
        {t("If you are not happy with how we handle your information, you can complain to the Information Commissioner's Office at")}{' '}
        <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer">ico.org.uk/make-a-complaint</a>{' '}
        {t('or on 0303 123 1113. We would like the chance to put things right first.')}
      </p>

      <h3>{t('Children')}</h3>
      <p>
        {t('EasyLearning is used by adults for the learners they support, many of whom are children. Accounts belong to adults. Learners never sign in and are never shown adverts.')}
      </p>

      <h3>{t('Things stored on your device')}</h3>
      <p>
        {t('EasyLearning stores only what it needs to work: your sign-in (until you close the tab), which learner you last chose, and your display settings. It uses no tracking cookies.')}
      </p>

      <h3>{t('Changes')}</h3>
      <p>
        {t('If we change how we use information, we will update this notice and tell you in EasyLearning before the change applies.')}
      </p>
    </div>
  );
}
